import { FC, useState, useMemo } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { Box, ButtonBase, Typography, useTheme } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay'
import { EventOutlined } from '@mui/icons-material'
import { useIntl } from 'react-intl'

import { useBoardsList, useBoardsListPosts } from 'services/boards/boards.hooks'
import HelpdeskEmptyState from 'components/Helpdesk/HelpdeskEmptyState'
import { calendarCardSx, calendarSx } from 'components/Helpdesk/FormPrimitives'

const Day: FC<PickersDayProps<Dayjs> & { events?: any[] }> = ({
  sx,
  events = [],
  day,
  ...props
}) => {
  const hasEvent = !!events.filter(({ eventDate }) => day.isSame(eventDate, 'day')).length

  return (
    <PickersDay
      {...props}
      day={day}
      sx={{
        ...sx,
        ':after': {
          content: "''",
          width: '5px',
          height: '5px',
          backgroundColor: '#527EF7',
          borderRadius: '50%',
          bottom: '5px',
          position: 'absolute',
          margin: '0 auto',
          display: hasEvent ? 'block' : 'none'
        }
      }}
    />
  )
}

// Board events calendar ("Panel Klienta 3.0" Komunikaty — Kalendarz view).
const WallCalendar: FC = () => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const [selectadDate, setSelectedDate] = useState<null | Dayjs>(null)
  const { data = [], isFetching, isLoading } = useBoardsList()
  const { boardsListPosts } = useBoardsListPosts(data.map(({ id }) => id))
  const navigate = useNavigate()

  const events = useMemo(
    () =>
      boardsListPosts.filter(({ eventDate }) => {
        if (!!selectadDate) {
          return selectadDate.isSame(eventDate, 'day')
        }

        return dayjs().isSame(eventDate, 'day')
      }),
    [selectadDate, boardsListPosts]
  )

  return (
    <Box>
      <Box sx={{ ...calendarCardSx, mb: '16px' }}>
        <DateCalendar
          value={selectadDate}
          onChange={(value) => setSelectedDate(value)}
          views={['day']}
          slots={{ day: Day }}
          slotProps={{
            day: {
              events: boardsListPosts
            } as any
          }}
          sx={calendarSx}
        />
      </Box>
      {!events.length && !isLoading && !isFetching ? (
        <HelpdeskEmptyState
          Icon={EventOutlined}
          title={formatMessage({
            id: 'wallCalendar.helperText',
            defaultMessage: 'Brak ogłoszeń na wybrany dzień.'
          })}
        />
      ) : (
        <Box sx={{ backgroundColor: '#fff', boxShadow: theme.r4r.cardShadow }}>
          {events.map(({ id, eventDate, title, board }, index) => (
            <ButtonBase
              key={id}
              onClick={() => navigate(`/board/${board.id}/${id}`)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                width: '100%',
                textAlign: 'left',
                padding: '14px 16px',
                borderBottom:
                  index === events.length - 1 ? 'none' : `1px solid ${theme.r4r.greyLight}`,
                ':hover': { backgroundColor: '#F9FAFB' }
              }}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 700,
                  color: theme.r4r.azure,
                  flexShrink: 0
                }}>
                {dayjs(eventDate).format('HH:mm')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: theme.r4r.ink,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                {title}
              </Typography>
            </ButtonBase>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default WallCalendar
